import React from "react";
import moment from "moment";
import get from "lodash.get";
import IntlUtils from "services/intl/utils";

import MapConfig from "services/map/MapConfig";
import paths from "routes/paths";

class CommonUtils {
  currentColorIndex: number = 0;

  formatNumber = (
    element: number,
    truncated: boolean = false
  ): number | string => {
    let value: string | number = element;

    if (typeof element === "number" && element.toLocaleString) {
      value = element.toLocaleString("fr");
      if (truncated) {
        const truncatedArray = value.split(" ");
        if (truncatedArray.length === 3) {
          value = `${truncatedArray[0]},${truncatedArray[1].charAt(0)} M`;
        }
        if (truncatedArray.length === 2) {
          value = `${truncatedArray[0]},${truncatedArray[1].charAt(0)} k`;
        }
      }
    }
    return value;
  };

  getClosestLowerValue = (arr: number[], value: number) => {
    return arr.reduce(
      (prev: number, curr: number) => (curr > value ? prev : curr),
      0
    );
  };

  getNewColor = (): string | String => {
    this.currentColorIndex += 1;
    return get(
      MapConfig,
      `flow.colorFactory[${
        (this.currentColorIndex - 1) %
        get(MapConfig, "flow.colorFactory", []).length
      }]`,
      get(MapConfig, "flow.point.fillColor.selected", "#69A73B")
    );
  };

  resetColorFactoryIndex = (): void => {
    this.currentColorIndex = 0;
  };

  splitDateFromDuration = (
    duration: number = 0,
    type: string
  ): { days: number; hours: number; minutes: number; seconds: number } => {
    let factor = 1000;

    if (type === "minutes") {
      factor = 1000 * 60;
    }

    if (type === "hours") {
      factor = 1000 * 60 * 60;
    }

    const momentDuration = moment.duration(duration * factor);

    return {
      days: momentDuration.days(),
      hours: momentDuration.hours(),
      minutes: momentDuration.minutes(),
      seconds: momentDuration.seconds(),
    };
  };

  humanizeDateFromDuration = (
    duration: number = 0,
    type: string = "seconds",
    intl: { messages: any },
    toggleIfZero: boolean = true
  ): string => {
    const { days, hours, minutes, seconds } = this.splitDateFromDuration(
      duration,
      type
    );
    let humanizedDate = "";

    if (days > 0 || toggleIfZero) {
      humanizedDate = `${humanizedDate}${days}${intl?.messages["components.timeSlider.day"]} `;
    }

    if (hours > 0 || (days > 0 && minutes > 0) || toggleIfZero) {
      humanizedDate = `${humanizedDate}${hours}${intl.messages["components.timeSlider.hour"]} `;
    }

    if (minutes > 0 || toggleIfZero) {
      humanizedDate = `${humanizedDate}${minutes}${intl.messages["components.timeSlider.minute"]} `;
    }

    if (seconds > 0 || toggleIfZero) {
      let secondsStr = seconds < 10 ? `0${seconds}` : seconds;
      humanizedDate = `${humanizedDate}${secondsStr}${intl.messages["components.timeSlider.second"]}`;
    }
    return humanizedDate;
  };

  humanizeMinutesToHours = (
    min: number = 0,
    format: boolean = true
  ): JSX.Element | string => {
    const intl = IntlUtils.getIntlInstance();

    const hours = Math.floor(min / 60);
    const minutes = min % 60;

    if (hours === 0) {
      if (!format) {
        return `${minutes} ${intl.messages["components.timeSlider.mins"]}`;
      }

      return (
        <>
          {minutes}
          <span style={{ fontSize: "14px", marginLeft: "3px" }}>
            {intl.messages["components.timeSlider.mins"]}
          </span>
        </>
      );
    }

    if (!format) {
      return (
        <>
          `${hours} ${intl.messages["components.timeSlider.hour"]} $
          {minutes < 10 ? `0${minutes}` : minutes}`
        </>
      );
    }
    return (
      <>
        {hours}
        <span style={{ fontSize: "16px" }}>
          {intl.messages["components.timeSlider.hour"]}
        </span>
        {minutes < 10 ? `0${minutes}` : minutes}
      </>
    );
  };

  humanizeSecondsToMinutes = (seconds: number = 0): JSX.Element | string => {
    const intl = IntlUtils.getIntlInstance();

    const minutes = Math.floor(seconds / 60);
    const secondsLeft = seconds % 60;

    if (minutes === 0) {
      return (
        <>
          {seconds}
          <span style={{ fontSize: "14px", marginLeft: "3px" }}>
            {intl.messages["components.timeSlider.seconds"]}
          </span>
        </>
      );
    }

    return (
      <>
        {minutes}
        <span style={{ fontSize: "14px", marginLeft: "0px" }}>
          {intl.messages["components.timeSlider.minute"]}
        </span>
        {secondsLeft !== 0 && (
          <>
            {secondsLeft < 10 ? `0${secondsLeft}` : secondsLeft}
            <span style={{ fontSize: "14px", marginLeft: "0px" }}>
              {intl.messages["components.timeSlider.second"]}
            </span>
          </>
        )}
      </>
    );
  };

  formatDuration = (duration: number): string | null => {
    if (duration !== 0 && !duration) return null;
    return moment.duration(duration * 1000).humanize();
  };

  formatDurationToHours = (duration: number): string | null => {
    if (duration !== 0 && !duration) return null;

    const hours = Math.floor(duration / 60);
    const minutes = duration % 60;

    if (hours === 0) {
      return `${minutes}min`;
    }

    return `${hours}h${minutes < 10 ? `0${minutes}` : minutes}`;
  };

  formatTime = (value: number): string | null => {
    if (!value) return null;
    const date = moment(value * 1000);
    return `${date.format("D MMMM - LT")}`;
  };

  iterateKeysAnDo = (
    obj: object,
    action: (value: string, index: number, array: string[]) => void
  ) => Object.keys(obj).forEach(action);

  isObject = (obj: any): boolean =>
    typeof obj === "object" && !Array.isArray(obj);

  buildQueryStringFromObject = (
    obj: any,
    prefix: string = ""
  ): string | null => {
    if (!obj) return null;
    const queryStringArray = Object.keys(obj).map((key) => {
      let cloneKey = key;

      const value = obj[key];
      const queryKey = `${prefix}${key}`;
      if (prefix) cloneKey = queryKey;

      if (this.isObject(value)) {
        return this.buildQueryStringFromObject(value, `${queryKey}.`);
      }

      if (Array.isArray(value)) {
        return `${cloneKey}=${value.join()}`;
      }

      return `${cloneKey}=${value}`;
    });

    return queryStringArray.filter((entry) => entry).join("&");
  };

  getModuleFromPath = (path: string): string | undefined => {
    const [module] =
      Object.entries(paths.front.tabs).find(([key, value]) => value === path) ||
      [];
    return module;
  };

  formatGraphWithTimeLimit = (attendanceData: any, limitTime: any): any => {
    let attendanceDataFiltered = {};
    Object.entries(attendanceData).forEach(([key, value]) => {
      const keyParsed = parseInt(key, 10);
      if (
        keyParsed >= limitTime.left_time_limit &&
        keyParsed <= limitTime.right_time_limit
      ) {
        attendanceDataFiltered = {
          ...attendanceDataFiltered,
          [key]: value,
        };
      }
    });
    return attendanceDataFiltered;
  };
}

export default new CommonUtils();
